import { createRouter, createWebHistory } from 'vue-router'
import type {
  LocationQueryValue,
  RouteLocationGeneric,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordRaw,
} from 'vue-router'
import { useUserStore } from '@/stores/user'
import { syncUserFromCookie, useInitUser } from '@/composables/useInitUser'
import { ABOUT_DICT } from '@/constants/about'
import DistributeView from '@/views/distribute/index.vue'
import FollowView from '@/views/follow/index.vue'
import MessagesView from '@/views/messages/index.vue'
import PlaceholderView from '@/views/placeholder/index.vue'
import ProfileView from '@/views/profile/index.vue'
import CartoonView from '@/views/cartoon/index.vue'
import SearchView from '@/views/search/index.vue'
import AboutView from '@/views/about/index.vue'
import AppView from '@/views/app/index.vue'
import NotFoundView from '@/views/not-found/index.vue'
import { APP_PAGE_META } from '@/constants/app'
import { APP_ROUTE_DEFINITIONS, DEFAULT_APP_ROUTE } from '@/constants/routes'
import { applyRouteHead } from '@/utils/head-meta'
import { buildRouteKeyWithoutLegacyPlayerQuery } from '@/utils/legacy-player'

const routeViewMap = {
  distribute: DistributeView,
  follow: FollowView,
  messages: MessagesView,
  placeholder: PlaceholderView,
  profile: ProfileView,
  cartoon: CartoonView,
  search: SearchView,
} as const

const staticRoutes = APP_ROUTE_DEFINITIONS.map((route) => ({
  path: route.path,
  name: route.name,
  component: routeViewMap[route.view],
  meta: {
    title: route.title,
    section: route.section,
    cacheKey: route.name,
    keepAlive: route.view === 'placeholder',
    requiresAuth: route.requiresAuth ?? false,
  },
}))

const profileView = routeViewMap.profile
const cartoonView = routeViewMap.cartoon

type LegacyRootQueryValue = LocationQueryValue | LocationQueryValue[] | undefined

function getFirstQueryValue(value: LegacyRootQueryValue) {
  const firstValue = Array.isArray(value) ? value[0] : value

  return firstValue ?? ''
}

function resolveRootRedirect(to: RouteLocationGeneric): RouteLocationRaw {
  const page = getFirstQueryValue(to.query.page)
  const role = getFirstQueryValue(to.query.role)

  if (page === 'role' && role) {
    const query = { ...to.query }

    delete query.page
    delete query.role

    return {
      name: 'CartoonView',
      params: {
        cartoonId: role,
      },
      query,
      hash: to.hash,
    }
  }

  return DEFAULT_APP_ROUTE
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: resolveRootRedirect,
  },
  {
    path: '/en',
    redirect: DEFAULT_APP_ROUTE,
  },
  {
    path: '/about',
    name: 'AboutView',
    component: AboutView,
    meta: {
      title: ABOUT_DICT.meta.title,
      description: ABOUT_DICT.meta.description,
      fullTitle: true,
      appShell: false,
      keepAlive: false,
      requiresAuth: false,
      lang: 'zh-CN',
      ogLocale: 'zh_CN',
      canonicalPath: '/about',
      alternatePaths: {
        'zh-CN': '/about',
        'x-default': '/about',
      },
    },
  },
  {
    path: '/app',
    name: 'AppView',
    component: AppView,
    meta: {
      title: APP_PAGE_META.title,
      description: APP_PAGE_META.description,
      fullTitle: true,
      appShell: false,
      keepAlive: false,
      requiresAuth: false,
      lang: 'zh-CN',
      ogLocale: 'zh_CN',
      canonicalPath: '/app',
      alternatePaths: {
        'zh-CN': '/app',
        'x-default': '/app',
      },
    },
  },
  ...staticRoutes,
  {
    path: '/me',
    redirect: '/mine',
  },
  {
    path: '/personal',
    name: 'LegacyPersonalView',
    component: profileView,
    meta: {
      title: '个人页',
      keepAlive: false,
      requiresAuth: false,
    },
  },
  {
    path: '/user/:userId',
    name: 'UserIdProfileView',
    component: profileView,
    meta: {
      title: '个人页',
      keepAlive: false,
      requiresAuth: false,
    },
  },
  {
    path: '/:uid([A-Za-z0-9_-]{1,16})',
    name: 'UidProfileView',
    component: profileView,
    meta: {
      title: '个人页',
      keepAlive: false,
      requiresAuth: false,
    },
  },
  {
    path: '/:userId(\\d{24})',
    name: 'RootUserIdProfileView',
    component: profileView,
    meta: {
      title: '个人页',
      keepAlive: false,
      requiresAuth: false,
    },
  },
  {
    path: '/character/:cartoonId',
    name: 'CartoonView',
    component: cartoonView,
    meta: {
      title: '角色',
      keepAlive: false,
      requiresAuth: false,
    },
  },
  {
    path: '/role',
    name: 'LegacyCartoonView',
    component: cartoonView,
    meta: {
      title: '角色',
      keepAlive: false,
      requiresAuth: false,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundView',
    component: NotFoundView,
    meta: {
      title: '页面走丢了',
      description: '咦，没有找到这个页面，回到发现页继续看看。',
      appShell: true,
      keepAlive: false,
      requiresAuth: false,
      robots: 'noindex, nofollow',
      ogType: 'website',
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (buildRouteKeyWithoutLegacyPlayerQuery(to) === buildRouteKeyWithoutLegacyPlayerQuery(from)) {
      return false
    }

    return savedPosition ?? { top: 0 }
  },
})

export const preloadRouteComponents = () => {
  return undefined
}

router.beforeEach(async (to: RouteLocationNormalized) => {
  if (to.meta.requiresAuth !== true) {
    return true
  }

  const userStore = useUserStore()

  if (syncUserFromCookie(userStore)) {
    return true
  }

  await useInitUser()

  return useUserStore().isLoggedIn() ? true : DEFAULT_APP_ROUTE
})

router.afterEach((to) => {
  applyRouteHead(to)
})

export default router
